import { GlButton, GlCollapse, GlIcon, GlLink, GlLoadingIcon, GlPopover, GlTooltipDirective, GlSprintf, GlToastMixin } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { createAlert } from '../../../alert.js?vue3';
import { __, n__, s__ } from '~/locale';
import WorkItemDetailPanel from '../../../work_items/components/work_item_detail_panel.vue?vue3';
import { convertToGraphQLId, getIdFromGraphQLId } from '~/graphql_shared/utils';
import { TYPENAME_MERGE_REQUEST } from '~/graphql_shared/constants';
import mergeRequestRelatedWorkItemsQuery from '~/sidebar/queries/merge_request_related_work_items.query.graphql';
import destroyMergeRequestWorkItemRelationMutation from '~/sidebar/queries/destroy_merge_request_work_item_relation.mutation.graphql';
import { DETAIL_VIEW_QUERY_PARAM_NAME, VIEW_CONTEXT } from '~/work_items/constants';
import { getParameterByName, removeParams, updateHistory } from '~/lib/utils/url_utility';
import { MR_WORK_ITEM_RELATIONSHIP_TYPES } from '~/sidebar/constants';
import RelatedWorkItemsAddForm from './related_work_items_add_form.vue?vue3';
export default {
    name: 'MRRelatedWorkItems',
    components: {
        GlButton,
        GlCollapse,
        GlIcon,
        GlLink,
        GlLoadingIcon,
        GlPopover,
        GlSprintf,
        WorkItemDetailPanel,
        RelatedWorkItemsAddForm
    },
    viewContext: VIEW_CONTEXT.drawerMergeRequest,
    i18n: {
        fromDescriptionTooltip: s__('WorkItem|This link comes from the merge request title, description, or a commit message. To remove the link, edit the text that references it.')
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        GlToastMixin
    ],
    inject: [
        'fullPath',
        'id'
    ],
    data () {
        return {
            activeItem: null,
            isCollapsed: true,
            params: null,
            mergeRequest: null,
            isAddModalVisible: false
        };
    },
    apollo: {
        mergeRequest: {
            context: {
                batchKey: 'MRSidebarData'
            },
            query: mergeRequestRelatedWorkItemsQuery,
            variables () {
                return {
                    id: convertToGraphQLId(TYPENAME_MERGE_REQUEST, this.id)
                };
            },
            update (data) {
                return data?.mergeRequest || null;
            },
            result () {
                this.checkDetailPanelParams();
            },
            error () {
                createAlert({
                    message: __('Something went wrong while fetching related work items.')
                });
            }
        }
    },
    computed: {
        isLoading () {
            return this.$apollo.queries.mergeRequest.loading;
        },
        mergeRequestGid () {
            return convertToGraphQLId(TYPENAME_MERGE_REQUEST, this.id);
        },
        allItems () {
            return (this.mergeRequest?.workItemRelations?.nodes || []).filter((i)=>i.workItem);
        },
        canAdminMergeRequest () {
            return this.mergeRequest?.userPermissions?.adminMergeRequest || false;
        },
        closingRelations () {
            return this.allItems.filter((i)=>i.linkType === MR_WORK_ITEM_RELATIONSHIP_TYPES.closing);
        },
        relatedRelations () {
            return this.allItems.filter((i)=>i.linkType === MR_WORK_ITEM_RELATIONSHIP_TYPES.related);
        },
        mentionedRelations () {
            return this.allItems.filter((i)=>i.linkType === MR_WORK_ITEM_RELATIONSHIP_TYPES.mentioned);
        },
        showCollapsedState () {
            return this.allItems.length > 2;
        },
        /**
     * The three relationship groups render identically, so they are driven from
     * a single list. Empty groups are dropped so the summary and the rendered
     * sections always stay in sync.
     */ relationSections () {
            return [
                {
                    key: 'closing',
                    title: __('Closing'),
                    relations: this.closingRelations
                },
                {
                    key: 'related',
                    title: __('Related'),
                    relations: this.relatedRelations
                },
                {
                    key: 'mentioned',
                    title: __('Mentioned'),
                    relations: this.mentionedRelations
                }
            ].filter((section)=>section.relations.length > 0);
        },
        collapsedSummary () {
            return this.relationSections.map((section)=>`${section.title} ${section.relations.length}`).join(', ');
        }
    },
    watch: {
        params (newParams) {
            const item = this.allItems.find((i)=>getIdFromGraphQLId(i.workItem.id) === newParams.id)?.workItem;
            if (item) {
                this.activeItem = item;
            } else {
                updateHistory({
                    url: removeParams([
                        DETAIL_VIEW_QUERY_PARAM_NAME
                    ])
                });
            }
        }
    },
    created () {
        window.addEventListener('popstate', this.checkDetailPanelParams);
    },
    beforeDestroy () {
        window.removeEventListener('popstate', this.checkDetailPanelParams);
    },
    methods: {
        getIdFromGraphQLId,
        /**
     * The add form links the items itself and keeps the modal open to show its
     * own error, so it only reports the successful links back here.
     */ handleLinked ({ count = 1 } = {}) {
            this.isAddModalVisible = false;
            this.$toast.show(n__('WorkItem|Linked item added', 'WorkItem|Linked items added', count));
        },
        async handleCreated () {
            this.isAddModalVisible = false;
            /**
       * workItemCreate does not return the MergeRequestWorkItemRelation node
       * (id, fromMrDescription) that the query is keyed on, so refetch to pick
       * up the new relation.
       */ await this.$apollo.queries.mergeRequest.refetch();
            /**
       * Show the toast only after the refetch has resolved, so a failed refetch
       * does not show a success message.
       */ this.$toast.show(s__('WorkItem|Linked item added'));
        },
        canRemoveRelation (relation) {
            return this.canAdminMergeRequest && Boolean(relation.id) && !relation.fromMrDescription;
        },
        /**
     * Relations generated from the description cannot be removed here, so we
     * explain why instead of silently hiding the remove button. Only shown to
     * users who can remove the other relations, since the tooltip would
     * otherwise point at an action they never have.
     */ showFromDescriptionIndicator (relation) {
            return this.canAdminMergeRequest && Boolean(relation.fromMrDescription);
        },
        async handleRemove (relation) {
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: destroyMergeRequestWorkItemRelationMutation,
                    variables: {
                        projectPath: this.fullPath,
                        iid: this.mergeRequest.iid,
                        ids: [
                            relation.id
                        ]
                    },
                    update: (cache, { data: result })=>this.removeRelationsFromCache(cache, result)
                });
                const errors = data?.mergeRequestDestroyWorkItemRelations?.errors || [];
                if (errors.length) {
                    createAlert({
                        message: errors.join(' ')
                    });
                    return;
                }
                this.$toast.show(s__('WorkItem|Linked item removed'));
            } catch (error) {
                createAlert({
                    message: s__('WorkItem|Something went wrong while removing the work item.'),
                    error,
                    captureError: true
                });
            }
        },
        removeRelationsFromCache (cache, result) {
            const removedIds = result?.mergeRequestDestroyWorkItemRelations?.removedRelationIds || [];
            if (!removedIds.length) {
                return;
            }
            const variables = {
                id: this.mergeRequestGid
            };
            const existing = cache.readQuery({
                query: mergeRequestRelatedWorkItemsQuery,
                variables
            });
            if (!existing?.mergeRequest) {
                return;
            }
            const removed = new Set(removedIds);
            const existingNodes = existing.mergeRequest.workItemRelations?.nodes || [];
            cache.writeQuery({
                query: mergeRequestRelatedWorkItemsQuery,
                variables,
                data: {
                    mergeRequest: {
                        ...existing.mergeRequest,
                        workItemRelations: {
                            __typename: 'MergeRequestWorkItemRelationConnection',
                            nodes: existingNodes.filter((node)=>!removed.has(node.id))
                        }
                    }
                }
            });
        },
        openDetailPanel (event, item) {
            if (event.metaKey || event.ctrlKey) {
                return;
            }
            event.preventDefault();
            this.activeItem = item;
        },
        checkDetailPanelParams () {
            const queryParam = getParameterByName(DETAIL_VIEW_QUERY_PARAM_NAME);
            if (!queryParam) {
                this.activeItem = null;
                return;
            }
            this.parseDetailPanelParams(queryParam);
        },
        parseDetailPanelParams (queryParam) {
            try {
                this.params = JSON.parse(atob(queryParam));
            } catch  {
                updateHistory({
                    url: removeParams([
                        DETAIL_VIEW_QUERY_PARAM_NAME
                    ])
                });
            }
        }
    }
};
